import { Button, Grid, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import * as React from "react";

import { LoadingWrapper } from "../../../components/LoadingWrapper";
import { Helpers } from "../../../utils/helpers";
import { fetchTournamentList, TournamentLayoutListItem, TournamentListResponse } from "../api";
import { renderTournamentStatus, renderTournamentValue, TournamentEmptyState, TournamentPageHeader, TournamentSourceNotice } from "../components";

const initialFilters = {
    query: "",
    difficulty: "",
    environmentId: "",
    status: "",
    page: 1,
    pageSize: 10,
};

export const TournamentLayouts = (props: any) => {
    const [filters, setFilters] = React.useState(initialFilters);
    const [response, setResponse] = React.useState<TournamentListResponse<TournamentLayoutListItem> | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        document.title = Helpers.documentTitle("Tournament Layouts");
        loadList(initialFilters);
    }, []);

    const loadList = (nextFilters = filters) => {
        setLoading(true);
        setError(null);

        fetchTournamentList<TournamentLayoutListItem>("/api/tournaments/layouts", nextFilters, props.activeEnv)
            .then(data => {
                setResponse(data);
                setFilters(nextFilters);
            })
            .catch(err => {
                setError(err?.response?.data?.message || err?.message || "Couldn't load tournament layouts.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleFilterChange = (key: string) => (event) => {
        setFilters({
            ...filters,
            [key]: event.target.value,
        });
    };

    const handleSearch = () => loadList({ ...filters, page: 1 });
    const handleClear = () => loadList(initialFilters);

    const handlePageChange = (_event: unknown, page: number) => {
        loadList({
            ...filters,
            page: page + 1,
        });
    };

    const handleRowsPerPageChange = (event) => {
        loadList({
            ...filters,
            page: 1,
            pageSize: parseInt(event.target.value, 10),
        });
    };

    const items = response?.data.items || [];
    const page = (response?.data.page || 1) - 1;
    const pageSize = response?.data.pageSize || filters.pageSize;
    const total = response?.data.total || 0;

    return <div className="contentPadding">
        <TournamentPageHeader
            title="Tournament / Layout"
        />

        <Paper className="root" elevation={0} style={{ marginTop: 16 }}>
            <div style={{ float: "left", width: "100%", padding: "22px 22px 0 22px" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Search"
                            value={filters.query}
                            onChange={handleFilterChange("query")}
                            placeholder="Layout ID, name, category"
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            select
                            label="Difficulty"
                            value={filters.difficulty}
                            onChange={handleFilterChange("difficulty")}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="easy">Easy</MenuItem>
                            <MenuItem value="medium">Medium</MenuItem>
                            <MenuItem value="hard">Hard</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth label="Environment ID" value={filters.environmentId} onChange={handleFilterChange("environmentId")} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            select
                            label="Status"
                            value={filters.status}
                            onChange={handleFilterChange("status")}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="review">Review</MenuItem>
                            <MenuItem value="draft">Draft</MenuItem>
                        </TextField>
                    </Grid>
                </Grid>

                <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <Button disableElevation variant="contained" color="primary" onClick={handleSearch}>
                        Search
                    </Button>
                    <Button disableElevation variant="contained" color="secondary" onClick={handleClear} style={{ marginLeft: 8 }}>
                        Clear
                    </Button>
                </div>
            </div>

            <div style={{ float: "left", width: "100%", padding: "0 22px 22px 22px" }}>
                <TournamentSourceNotice meta={response?.meta} />

                <LoadingWrapper loading={loading} error={error}>
                    {items.length === 0 ? <TournamentEmptyState
                        title="No layouts found"
                        description="Try a broader filter or connect the adapter to the tournament layout source."
                    /> : <React.Fragment>
                        <Table className="min-table-size">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Layout ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Difficulty</TableCell>
                                    <TableCell>Environment</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Map size</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Usage count</TableCell>
                                    <TableCell>Updated at</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map(item => <TableRow hover key={item.id}>
                                    <TableCell>
                                        <Button size="small" color="primary" onClick={() => props.history.push(`/tournament/layouts/${item.id}`)}>
                                            {item.id}
                                        </Button>
                                    </TableCell>
                                    <TableCell>{renderTournamentValue(item.name)}</TableCell>
                                    <TableCell>{renderTournamentValue(item.difficulty)}</TableCell>
                                    <TableCell>{renderTournamentValue(item.environmentId)}</TableCell>
                                    <TableCell>{renderTournamentStatus(item.status)}</TableCell>
                                    <TableCell>{renderTournamentValue(item.mapSize)}</TableCell>
                                    <TableCell>{renderTournamentValue(item.category)}</TableCell>
                                    <TableCell>{item.usageCount}</TableCell>
                                    <TableCell>{item.updatedAt ? Helpers.formatDateWithTime(item.updatedAt, true) : "n/a"}</TableCell>
                                    <TableCell>
                                        <Button size="small" variant="outlined" onClick={() => props.history.push(`/tournament/layouts/${item.id}`)}>
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>

                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            rowsPerPage={pageSize}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[5, 10, 20]}
                        />
                    </React.Fragment>}
                </LoadingWrapper>
            </div>
        </Paper>
    </div>;
};
